import { useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { filterObject, handleFormErrors } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { SYSTEMS } from "@/utils/permissions";
import { useSystem } from "@/utils/hooks/useLocation";

// ******* Queries *******

export const useGetOneHouseExaminationItemQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["one-house-examination-item", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/house-examination-items/${id}`),
    enabled: !!id,
  });
};

/** Cache for 5 minutes so basic data isn't refetched on every mount/focus (reference data). */
const BASIC_DATA_STALE_TIME = 5 * 60 * 1000;

export const useBasicDataQuery = ({ enabled = false } = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["basic-data"],
    queryFn: () => axiosProtected.get("basic-data"),
    enabled,
    staleTime: BASIC_DATA_STALE_TIME,
  });
};
export const useJobSectorsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["job-sectors", params],
    queryFn: () => axiosProtected.get("hr/job-sectors", { params }),
  });
};

export const useGetOneJobSectorQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-job-sector", id],
    queryFn: () => axiosProtected.get(`hr/job-sectors/${id}`),
    enabled: !!id,
  });
};

// banks
export const useBanksQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["banks", params],
    queryFn: () => axiosProtected.get("banks", { params }),
  });
};
export const useGetOneBankQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-bank", id],
    queryFn: () => axiosProtected.get(`banks/${id}`),
  });
};
// qualifications
export const useQualificationsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["qualifications", params],
    queryFn: () => axiosProtected.get("qualifications", { params }),
  });
};
export const useGetOneQualificationQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-qualification", id],
    queryFn: () => axiosProtected.get(`qualifications/${id}`),
  });
};

// departments (seasonal_hr)
export const useDepartmentsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["departments", params],
    queryFn: () => axiosProtected.get("hr/departments", { params }),
  });
};

export const useGetOneDepartmentQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-department", id],
    queryFn: () => axiosProtected.get(`hr/departments/${id}`),
    enabled: !!id,
  });
};

// jobs (seasonal_hr)
export const useJobsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["jobs", params],
    queryFn: () => axiosProtected.get("hr/job-opportunities", { params }),
  });
};

export const useGetOneJobQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-job", id],
    queryFn: () => axiosProtected.get(`hr/job-opportunities/${id}`),
    enabled: !!id,
  });
};

// cities
export const useCitiesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["cities", params],
    queryFn: () => axiosProtected.get("cities", { params }),
  });
};
export const useGetOneCityQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-city", id],
    queryFn: () => axiosProtected.get(`cities/${id}`),
  });
};

export const useActiveNationalitiesQuery = (queryOptions = {}) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useQuery({
    queryKey: ["active-nationalities"],
    queryFn: async () => {
      const response = await axiosProtected.get("nationalities/active");
      return response?.data?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    onError: (err) => {
      toast.error(err?.message || t("ACTIVE_NATIONALITIES_FETCH_FAILED"));
    },
    ...queryOptions,
  });
};

const normalizeOption = (entity = {}) => {
  const { id, nameAr = "", nameEn = "" } = entity;
  const value = id?.toString?.() ?? id;
  const labelAr = nameAr?.trim?.() || "";
  const labelEn = nameEn?.trim?.() || "";
  const q = [labelAr, labelEn].filter(Boolean).join(",");

  return {
    value,
    labelAr,
    labelEn,
    q,
    raw: entity,
  };
};

export const useMissionsByCountryQuery = (countryId, queryOptions = {}) => {
  const { axiosProtected } = useAxios();
  const {currentSystem} = useSystem();
  const { t } = useTranslation();

  return useQuery({
    queryKey: ["missions-by-country", countryId, currentSystem],
    queryFn: async () => {
      if (!countryId) {
        return [];
      }

      const response = await axiosProtected.get(`${currentSystem}/missions`, {
        params: { country_id: countryId },
      });
      const missions = response?.data?.data?.missions ?? [];
      return missions.map((mission) => normalizeOption(mission));
    },
    enabled: Boolean(countryId),
    staleTime: 2 * 60 * 1000,
    onError: (err) => {
      toast.error(err?.message || t("MISSIONS_FETCH_FAILED"));
    },
    ...queryOptions,
  });
};

export const useMissionCompaniesQuery = (missionId, queryOptions = {}) => {
  const { axiosProtected } = useAxios();
  const {currentSystem} = useSystem();
  const { t } = useTranslation();

  return useQuery({
    queryKey: ["mission-companies", missionId, currentSystem],
    queryFn: () => axiosProtected.get(`${currentSystem}/missions/${missionId}/companies`),
    enabled: Boolean(missionId),
    onError: (err) => {
      toast.error(err?.message || t("MISSION_COMPANIES_FETCH_FAILED"));
    },
    ...queryOptions,
  });
};

// ******* Mutations *******
// house examination items
export const useCreateHouseExaminationItemMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/house-examination-items`, data),
    mutationKey: "createHouseExaminationItem",
    onSuccess: (data) => {
      const houseExaminationItem = data?.data?.data;
      toast.success(t("CREATE_HOUSE_EXAMINATION_ITEM_SUCCESS"));
      client.setQueryData(
        ["one-house-examination-item", houseExaminationItem.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/house_examination_items/${houseExaminationItem.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "CREATE_HOUSE_EXAMINATION_ITEM_FAILED"
      );
    },
  });
};


export const useDeleteJobSectorMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => axiosProtected.delete(`hr/job-sectors/${id}`),
    mutationKey: "deleteJobSector",
    onSuccess: () => {
      toast.success(t("DELETE_JOB_SECTOR_SUCCESS"));
      client.invalidateQueries({ queryKey: ["job-sectors"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("DELETE_JOB_SECTOR_FAILED"));
    },
  });
};

const getJobSectorsBasePath = () =>
  typeof window !== "undefined" && window.location.pathname.includes("seasonal_hr")
    ? "/app/seasonal_hr/basic-data/job-sectors"
    : "/app/makkah/basic-data/job-sectors";

export const useCreateJobSectorMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("hr/job-sectors", data),
    mutationKey: "createJobSector",
    onSuccess: (data) => {
      const jobSector = data?.data?.data;
      toast.success(t("CREATE_JOB_SECTOR_SUCCESS"));
      if (jobSector?.id) {
        navigate(`${getJobSectorsBasePath()}/${jobSector.id}/view`);
      }
    },
  });
};

export const useUpdateJobSectorMutation = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`hr/job-sectors/${id}`, data),
    mutationKey: ["updateJobSector", id],
    onSuccess: () => {
      toast.success(t("UPDATE_JOB_SECTOR_SUCCESS"));
      navigate(`${getJobSectorsBasePath()}/${id}/view`);
    },
  });
};

export const useDeleteHouseExaminationItemMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({ id }) =>
      axiosProtected.delete(`${currentSystem}/house-examination-items/delete/${id}`),
    mutationKey: "deleteHouseExaminationItem",
    onSuccess: () => {
      toast.success(t("DELETE_HOUSE_EXAMINATION_ITEM_SUCCESS"));
    },
    onError: (err) => {
      toast.error(err?.message || t("DELETE_HOUSE_EXAMINATION_ITEM_FAILED"));
    },
  });
};

export const useUpdateHouseExaminationItemMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/house-examination-items/${id}`, data),
    mutationKey: ["updateHouseExaminationItem", id],
    onSuccess: (data) => {
      const houseExaminationItem = data?.data?.data;
      toast.success(t("UPDATE_HOUSE_EXAMINATION_ITEM_SUCCESS"));
      client.setQueryData(
        ["one-house-examination-item", houseExaminationItem.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/house_examination_items/${houseExaminationItem.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "UPDATE_HOUSE_EXAMINATION_ITEM_FAILED"
      );
    },
  });
};

// examination periods
export const useExaminationPeriodsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["examination-periods", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/examination-periods`, { params: { ...params, system: currentSystem } }),
  });
};

export const useGetOneExaminationPeriodQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-examination-period", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/examination-periods/${id}`),
    enabled: !!id,
  });
};

export const useCreateExaminationPeriodMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/examination-periods`, {...data, system: currentSystem}),
    mutationKey: "createExaminationPeriod",
    onSuccess: (data) => {
      const examinationPeriod = data?.data?.data;
      toast.success(t("CREATE_EXAMINATION_PERIOD_SUCCESS"));
      client.setQueryData(
        ["one-examination-period", examinationPeriod.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/examination_periods/${examinationPeriod.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "CREATE_EXAMINATION_PERIOD_FAILED"
      );
    },
  });
};

export const useUpdateExaminationPeriodMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/examination-periods/${id}`, data),
    mutationKey: ["updateExaminationPeriod", id],
    onSuccess: (data) => {
      const examinationPeriod = data?.data?.data;
      toast.success(t("UPDATE_EXAMINATION_PERIOD_SUCCESS"));
      client.setQueryData(
        ["one-examination-period", examinationPeriod.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/examination_periods/${examinationPeriod.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "UPDATE_EXAMINATION_PERIOD_FAILED"
      );
    },
  });
};

export const useDeleteExaminationPeriodMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ id }) =>
      axiosProtected.delete(`${currentSystem}/examination-periods/delete/${id}`),
    mutationKey: "deleteExaminationPeriod",
    onSuccess: () => {
      toast.success(t("DELETE_EXAMINATION_PERIOD_SUCCESS"));
      client.invalidateQueries({ queryKey: ["examination-periods"] });
    },
    onError: (err) => {
      toast.error(err?.message || t("DELETE_EXAMINATION_PERIOD_FAILED"));
    },
  });
};

// period codes
export const usePeriodCodesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["period-codes", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/period-codes`, { params: { ...params, system: currentSystem } }),
  });
};

export const useGetOnePeriodCodeQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-period-code", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/period-codes/${id}`),
    enabled: !!id,
  });
};

export const useCreatePeriodCodeMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/period-codes`, {...data, system: currentSystem}),
    mutationKey: "createPeriodCode",
    onSuccess: (data) => {
      const periodCode = data?.data?.data;
      toast.success(t("CREATE_PERIOD_CODE_SUCCESS"));
      client.setQueryData(
        ["one-period-code", periodCode.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/period_codes/${periodCode.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "CREATE_PERIOD_CODE_FAILED"
      );
    },
  });
};

export const useUpdatePeriodCodeMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/period-codes/${id}`, data),
    mutationKey: ["updatePeriodCode", id],
    onSuccess: (data) => {
      const periodCode = data?.data?.data;
      toast.success(t("UPDATE_PERIOD_CODE_SUCCESS"));
      client.setQueryData(
        ["one-period-code", periodCode.id],
        data
      );
      navigate(
        `/app/${currentSystem}/basic-data/period_codes/${periodCode.id}/view`
      );
    },
    onError: (err) => {
      handleFormErrors(
        err,
        setError,
        t,
        "UPDATE_PERIOD_CODE_FAILED"
      );
    },
  });
};

export const useDeletePeriodCodeMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const client = useQueryClient();
  const { currentSystem } = useSystem();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: ({ id }) =>
      axiosProtected.delete(`${system}/period-codes/delete/${id}`),
    mutationKey: "deletePeriodCode",
    onSuccess: () => {
      toast.success(t("DELETE_PERIOD_CODE_SUCCESS"));
      client.invalidateQueries({ queryKey: ["period-codes"] });
    },
    onError: (err) => {
      toast.error(err?.message || t("DELETE_PERIOD_CODE_FAILED"));
    },
  });
};

export const useCreateBankMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("banks", data),
    mutationKey: "createBank",
    onSuccess: () => {
      toast.success(t("CREATE_BANK_SUCCESS"));
      navigate("/app/makkah/basic-data/banks");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_BANK_FAILED");
    },
  });
};
export const useUpdateBankMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`banks/${data.id}`, data),
    mutationKey: "updateBank",
    onSuccess: () => {
      toast.success(t("UPDATE_BANK_SUCCESS"));
      navigate("/app/makkah/basic-data/banks");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_BANK_FAILED");
    },
  });
};
export const useDeleteBankMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`banks/${id}`),
    mutationKey: "deleteBank",
    onSuccess: () => {
      toast.success(t("DELETE_BANK_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_BANK_FAILED");
    },
  });
};
// Qualifications
export const useCreateQualificationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("qualifications", data),
    mutationKey: "createQualification",
    onSuccess: () => {
      toast.success(t("CREATE_QUALIFICATION_SUCCESS"));
      navigate("/app/makkah/basic-data/qualifications");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_QUALIFICATION_FAILED");
    },
  });
};

export const useUpdateQualificationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`qualifications/${data.id}`, data),
    mutationKey: "updateQualification",
    onSuccess: () => {
      toast.success(t("UPDATE_QUALIFICATION_SUCCESS"));
      navigate("/app/makkah/basic-data/qualifications");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_QUALIFICATION_FAILED");
    },
  });
};
export const useDeleteQualificationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`qualifications/${id}`),
    mutationKey: "deleteQualification",
    onSuccess: () => {
      toast.success(t("DELETE_QUALIFICATION_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_QUALIFICATION_FAILED");
    },
  });
};

const getDepartmentsRedirectPath = () =>
  typeof window !== "undefined" && window.location.pathname.includes("seasonal_hr")
    ? "/app/seasonal_hr/basic-data/departments"
    : "/app/makkah/basic-data/departments";

const getJobsRedirectPath = () =>
  typeof window !== "undefined" && window.location.pathname.includes("seasonal_hr")
    ? "/app/seasonal_hr/basic-data/jobs"
    : "/app/makkah/basic-data/jobs";

export const useCreateDepartmentMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("hr/departments", data),
    mutationKey: "createDepartment",
    onSuccess: () => {
      toast.success(t("CREATE_DEPARTMENT_SUCCESS"));
      navigate(getDepartmentsRedirectPath());
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_DEPARTMENT_FAILED");
    },
  });
};

export const useUpdateDepartmentMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`hr/departments/${data.id}`, data),
    mutationKey: "updateDepartment",
    onSuccess: () => {
      toast.success(t("UPDATE_DEPARTMENT_SUCCESS"));
      navigate(getDepartmentsRedirectPath());
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_DEPARTMENT_FAILED");
    },
  });
};

export const useDeleteDepartmentMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`hr/departments/${id}`),
    mutationKey: "deleteDepartment",
    onSuccess: () => {
      toast.success(t("DELETE_DEPARTMENT_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_DEPARTMENT_FAILED");
    },
  });
};

export const useCreateJobMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("hr/job-opportunities", data),
    mutationKey: "createJob",
    onSuccess: () => {
      toast.success(t("CREATE_JOB_SUCCESS"));
      navigate(getJobsRedirectPath());
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_JOB_FAILED");
    },
  });
};

export const useUpdateJobMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) =>
      axiosProtected.put(`hr/job-opportunities/${data.id}`, data),
    mutationKey: "updateJob",
    onSuccess: () => {
      toast.success(t("UPDATE_JOB_SUCCESS"));
      navigate(getJobsRedirectPath());
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_JOB_FAILED");
    },
  });
};

export const useDeleteJobMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`hr/job-opportunities/${id}`),
    mutationKey: "deleteJob",
    onSuccess: () => {
      toast.success(t("DELETE_JOB_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_JOB_FAILED");
    },
  });
};

// cities
export const useCreateCityMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("cities", data),
    mutationKey: "createCity",
    onSuccess: () => {
      toast.success(t("CREATE_CITY_SUCCESS"));
      navigate("/app/makkah/basic-data/cities");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_CITY_FAILED");
    },
  });
};

export const useUpdateCityMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`cities/${data.id}`, data),
    mutationKey: "updateCity",
    onSuccess: () => {
      toast.success(t("UPDATE_CITY_SUCCESS"));
      navigate("/app/makkah/basic-data/cities");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_CITY_FAILED");
    },
  });
};
export const useDeleteCityMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`cities/${id}`),
    mutationKey: "deleteCity",
    onSuccess: () => {
      toast.success(t("DELETE_CITY_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_CITY_FAILED");
    },
  });
};

// ports
export const usePortsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["ports", params],
    queryFn: () => axiosProtected.get("ports", { params }),
  });
};
export const useGetOnePortQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-port", id],
    queryFn: () => axiosProtected.get(`ports/${id}`),
  });
};
export const useCreatePortMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("ports", data),
    mutationKey: "createPort",
    onSuccess: () => {
      toast.success(t("CREATE_PORT_SUCCESS"));
      navigate("/app/makkah/basic-data/ports");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_PORT_FAILED");
    },
  });
};
export const useUpdatePortMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`ports/${data.id}`, data),
    mutationKey: "updatePort",
    onSuccess: () => {
      toast.success(t("UPDATE_PORT_SUCCESS"));
      navigate("/app/makkah/basic-data/ports");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_PORT_FAILED");
    },
  });
};
export const useDeletePortMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`ports/${id}`),
    mutationKey: "deletePort",
    onSuccess: () => {
      toast.success(t("DELETE_PORT_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_PORT_FAILED");
    },
  });
};

// directions
export const useDirectionsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["directions", params],
    queryFn: () => axiosProtected.get("directions", { params }),
  });
};
export const useGetOneDirectionQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-direction", id],
    queryFn: () => axiosProtected.get(`directions/${id}`),
  });
};
export const useCreateDirectionMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {currentSystem} = useSystem();
  return useMutation({
    mutationFn: (data) => axiosProtected.post("directions", data),
    mutationKey: "createDirection",
    onSuccess: () => {
      toast.success(t("CREATE_DIRECTION_SUCCESS"));
      navigate(`/app/${currentSystem}/basic-data/directions`);
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_DIRECTION_FAILED");
    },
  });
};
export const useUpdateDirectionMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {currentSystem} = useSystem();
  return useMutation({
    mutationFn: (data) => axiosProtected.put(`directions/${data.id}`, data),
    mutationKey: "updateDirection",
    onSuccess: () => {
      toast.success(t("UPDATE_DIRECTION_SUCCESS"));
      navigate(`/app/${currentSystem}/basic-data/directions`);
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_DIRECTION_FAILED");
    },
  });
};
export const useDeleteDirectionMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`directions/${id}`),
    mutationKey: "deleteDirection",
    onSuccess: () => {
      toast.success(t("DELETE_DIRECTION_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_DIRECTION_FAILED");
    },
  });
};

// service-centers (supports makkah / madina via system prefix in API URL)
const serviceCentersBase = (system = "makkah") => `${system}/service-centers`;

export const useServiceCentersQuery = (params = {}, options = {}) => {
  const { system = "makkah" } = options;
  let systemName = system === "makkah" ? "makkah" : "madina";
  const { axiosProtected } = useAxios();
  const base = serviceCentersBase(systemName);

  return useQuery({
    queryKey: ["service-centers", system, params],
    queryFn: () => axiosProtected.get(base, { params }),
  });
};
export const useEmployeesOptionsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["employees-options", params],
    queryFn: () => axiosProtected.get("users/employees", { params }),
  });
};
export const useGetOneServiceCenterQuery = ({ id }, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const base = serviceCentersBase(system);

  return useQuery({
    queryKey: ["one-service-center", system, id],
    queryFn: () => axiosProtected.get(`${base}/${id}`),
    enabled: !!id,
  });
};
export const useCreateServiceCenterMutation = (setError, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  let systemName = system === "makkah" ? "makkah" : "madina";
  const base = serviceCentersBase(systemName);

  return useMutation({
    mutationFn: (data) => axiosProtected.post(base, data),
    mutationKey: ["createServiceCenter", systemName],
    onSuccess: () => {
      toast.success(t("CREATE_SERVICE_CENTER_SUCCESS"));
      navigate(`/app/${system}/basic-data/service-centers`);
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_SERVICE_CENTER_FAILED");
    },
  });
};
export const useUpdateServiceCenterMutation = (setError, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  let systemName = system === "makkah" ? "makkah" : "madina";
  const base = serviceCentersBase(systemName);

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`${base}/${data.id}`, data),
    mutationKey: ["updateServiceCenter", systemName],
    onSuccess: () => {
      toast.success(t("UPDATE_SERVICE_CENTER_SUCCESS"));
      navigate(`/app/${system}/basic-data/service-centers`);
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_SERVICE_CENTER_FAILED");
    },
  });
};
export const useDeleteServiceCenterMutation = (setError, options = {}) => {
  const { system = "makkah" } = options;
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  let systemName = system === "makkah" ? "makkah" : "madina";
  const base = serviceCentersBase(systemName);

  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`${base}/${id}`),
    mutationKey: ["deleteServiceCenter", systemName],
    onSuccess: () => {
      toast.success(t("DELETE_SERVICE_CENTER_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_SERVICE_CENTER_FAILED");
    },
  });
};

// transportations
export const useTransportationsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["transportations", params],
    queryFn: () => axiosProtected.get("transportations", { params }),
  });
};
export const useGetOneTransportationQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-transportation", id],
    queryFn: () => axiosProtected.get(`transportations/${id}`),
  });
};
export const useCreateTransportationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("transportations", data),
    mutationKey: "createTransportation",
    onSuccess: () => {
      toast.success(t("CREATE_TRANSPORTATION_SUCCESS"));
      navigate("/app/makkah/basic-data/transportations");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_TRANSPORTATION_FAILED");
    },
  });
};
export const useUpdateTransportationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) =>
      axiosProtected.put(`transportations/${data.id}`, data),
    mutationKey: "updateTransportation",
    onSuccess: () => {
      toast.success(t("UPDATE_TRANSPORTATION_SUCCESS"));
      navigate("/app/makkah/basic-data/transportations");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_TRANSPORTATION_FAILED");
    },
  });
};
export const useDeleteTransportationMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`transportations/${id}`),
    mutationKey: "deleteTransportation",
    onSuccess: () => {
      toast.success(t("DELETE_TRANSPORTATION_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_TRANSPORTATION_FAILED");
    },
  });
};

// airlines
export const useAirlinesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["airlines", params],
    queryFn: () => axiosProtected.get("airlines", { params }),
  });
};
export const useGetOneAirlineQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-airline", id],
    queryFn: () => axiosProtected.get(`airlines/${id}`),
  });
};
export const useCreateAirlineMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("airlines", data),
    mutationKey: "createAirline",
    onSuccess: () => {
      toast.success(t("CREATE_AIRLINE_SUCCESS"));
      navigate("/app/makkah/basic-data/airlines");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_AIRLINE_FAILED");
    },
  });
};
export const useUpdateAirlineMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`airlines/${data.id}`, data),
    mutationKey: "updateAirline",
    onSuccess: () => {
      toast.success(t("UPDATE_AIRLINE_SUCCESS"));
      navigate("/app/makkah/basic-data/airlines");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_AIRLINE_FAILED");
    },
  });
};
export const useDeleteAirlineMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`airlines/${id}`),
    mutationKey: "deleteAirline",
    onSuccess: () => {
      toast.success(t("DELETE_AIRLINE_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_AIRLINE_FAILED");
    },
  });
};

// states
export const useStatesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["states", params],
    queryFn: () => axiosProtected.get("states", { params }),
  });
};
export const useGetOneStateQuery = ({ id }) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-state", id],
    queryFn: () => axiosProtected.get(`states/${id}`),
  });
};
export const useCreateStateMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.post("states", data),
    mutationKey: "createState",
    onSuccess: () => {
      toast.success(t("CREATE_STATE_SUCCESS"));
      navigate("/app/makkah/basic-data/states");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_STATE_FAILED");
    },
  });
};
export const useUpdateStateMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data) => axiosProtected.put(`states/${data.id}`, data),
    mutationKey: "updateState",
    onSuccess: () => {
      toast.success(t("UPDATE_STATE_SUCCESS"));
      navigate("/app/makkah/basic-data/states");
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "UPDATE_STATE_FAILED");
    },
  });
};
export const useDeleteStateMutation = (setError) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id) => axiosProtected.delete(`states/${id}`),
    mutationKey: "deleteState",
    onSuccess: () => {
      toast.success(t("DELETE_STATE_SUCCESS"));
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "DELETE_STATE_FAILED");
    },
  });
};


// Simulate API delay (still used by other mock functions)
// const simulateDelay = (ms = 800) => {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// };

export const useInitialGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  // Build query parameters
  const queryParams = new URLSearchParams();

  // Add search parameter if provided
  if (params.query && params.query.trim()) {
    queryParams.append("query", params.query.trim());
  }

  // Add page parameter if provided
  if (params.page) {
    queryParams.append("page", params.page);
  }

  // Add per_page parameter if provided
  if (params.per_page) {
    queryParams.append("per_page", params.per_page);
  }

  const queryString = queryParams.toString();
  const url = queryString
    ? `${currentSystem}/makkah-groups/main-group?${queryString}`
    : `${currentSystem}/makkah-groups/main-group`;

  return useQuery({
    queryKey: ["initial-groups", currentSystem, params],
    queryFn: async () => {
      const response = await axiosProtected.get(url);

      // Transform API response to match component expectations
      // API returns: { success: true, data: [...] }
      // Component expects: { data: { data: [...], meta: { per_page: [...], total: [...] } } }
      const apiData = response.data?.data || [];
      const total = apiData.length;
      const perPage = params.per_page || 10;

      return {
        data: {
          data: apiData,
          meta: {
            per_page: [perPage],
            total: [total],
          },
        },
      };
    },
  });
};

// Create initial group mutation
export const useCreateInitialGroupMutation = (setError, currentSystem) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSystem : currentSystemFromHook } = useSystem();

  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystemFromHook}/makkah-groups/main-group`, data),
    mutationKey: "createInitialGroup",
    onSuccess: (data) => {
      const id = data.data.data.id;
      toast.success(t("CREATE_INITIAL_GROUP_SUCCESS"));
      !!id && navigate(`/app/${currentSystemFromHook}/groups-upcoming/${id}/view`);
    },
    onError: (err) => {
      handleFormErrors(err, setError, t, "CREATE_INITIAL_GROUP_FAILED");
    },
  });
};

// Update initial group mutation
export const useUpdateInitialGroupMutation = ({ setError }) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (payload) => {
      return axiosProtected.post(
        `${currentSystem}/makkah-groups/update/main-group/${payload.id}`,
        payload
      );
    },
    mutationKey: "updateInitialGroup",
    onSuccess: ($_, variables) => {
      toast.success(t("UPDATE_INITIAL_GROUP_SUCCESS"));
      queryClient.invalidateQueries({
        queryKey: ["one-initial-group", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["initial-groups"] });
      navigate(`/app/${currentSystem}/groups-upcoming/${variables.id}/view`);
    },
    onError: (err) => {
      console.error("Initial group update error:", err);
      if (setError) {
        handleFormErrors(err, setError, t, "UPDATE_INITIAL_GROUP_FAILED");
      }
      toast.error(
        err?.response?.data?.message || t("UPDATE_INITIAL_GROUP_FAILED")
      );
    },
  });
};



export const useMakkahPilgrimGroupNameMappings = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const query = filterObject(params);

  return useQuery({
    queryKey: ["makkah-pilgrim-group-name-mappings", currentSystem, query],
    queryFn: () => axiosProtected.get(`${currentSystem}/pilgrim-group-name-mappings`, { params: query }),
  });


}
export const useFlightTerminalsQuery = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["useFlightTerminalsQuery"],
    queryFn: () => axiosProtected.get(`${currentSystem}/flight-details/terminals`),
  });

}

export const usePilgrimGroupNames = ({
  company_id,
  mission_id,
  enabled: enabledProp = true,
} = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  const query = filterObject({ company_id, mission_id });

  return useQuery({
    queryKey: ["pilgrim-group-names", query],
    queryFn: () => axiosProtected.get(`${currentSystem}/pilgrim-group-names`, { params: query }),
    enabled:
      enabledProp !== false &&
      (!!company_id || !!mission_id),
  });
}
export const useMakkahPilgrimGroupNameMappingsMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationKey: ["create-makkah-pilgrim-group-name-mappings"],
    mutationFn: (payload) => axiosProtected.post(`${currentSystem}/pilgrim-group-name-mappings`, payload),
    onSuccess: (data) => {
      const id = data.data.data[0].id;
      toast.success(t("CREATE_PILGRIM_GROUP_NAME_MAPPINGS_SUCCESS"));
      !!id && navigate(`/app/${currentSystem}/basic-data/makkah-pilgrim-group-name-mappings/${id}/view`);
    },
  });
}
export const useUpdateMakkahPilgrimGroupNameMappingMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationKey: ["update-makkah-pilgrim-group-name-mapping", id],
    mutationFn: (payload) => axiosProtected.put(`${currentSystem}/pilgrim-group-name-mappings/${id}`, payload),
    onSuccess: () => {
      toast.success(t("UPDATE_PILGRIM_GROUP_NAME_MAPPINGS_SUCCESS"));
      navigate(`/app/${currentSystem}/basic-data/makkah-pilgrim-group-name-mappings/${id}/view`);
    },
  });
}
export const useOneMakkahPilgrimGroupNameMappingQuery = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["one-makkah-pilgrim-group-name-mapping", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/pilgrim-group-name-mappings/${id}`),
    enabled: !!id,
  });
}
export const useDeleteMakkahPilgrimGroupNameMappingMutation = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationKey: ["delete-makkah-pilgrim-group-name-mapping", id],
    mutationFn: ({ id }) => axiosProtected.delete(`${currentSystem}/pilgrim-group-name-mappings/${id}`),
    onSuccess: () => {
      toast.success(t("DELETE_PILGRIM_GROUP_NAME_MAPPINGS_SUCCESS"));
      queryClient.invalidateQueries({ queryKey: ["makkah-pilgrim-group-name-mappings"] });
    },
    onError: (err) => {
      toast.error(t("DELETE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED"));
    },
  });
}

export const useInfiniteMakkahPilgrimGroupNameMappingsQuery = ({
  search,
  ...params
}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const query = filterObject({
    per_page: 20,
    search,
    ...params,
  });

  return useInfiniteQuery({
    queryKey: ["infinite-makkah-pilgrim-group-name-mappings", currentSystem, query],
    queryFn: ({ pageParam = 1 }) =>
      axiosProtected.get(`${currentSystem}/pilgrim-group-name-mappings`, {
        params: {
          page: pageParam,
          ...query
        }
      }),
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage?.data?.data?.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};